// src/Components/StripeCheckout.jsx
import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { supabase } from '../shared/supabase';
import { Loader2, Lock, X, CheckCircle } from 'lucide-react';

// ============================================================
// Init Stripe (publishable key — safe to be in frontend)
// ============================================================
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// ============================================================
// Create checkout session via Supabase Edge Function
// Returns { clientSecret, subscriptionId }
// ============================================================
export const createCheckoutSession = async (userId, priceId) => {
  const { data: { session } } = await supabase.auth.getSession();

  const response = await fetch(
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-checkout-session`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
        apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ priceId }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create checkout session');
  }

  return response.json(); // { clientSecret, subscriptionId }
};

// ============================================================
// Handle successful payment (called from App.js on redirect)
// ============================================================
export const handlePaymentSuccess = async (sessionId, userId) => {
  try {
    const { data: existingProfile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (!existingProfile) {
      await supabase.from('profiles').insert([{
        id: userId,
        is_premium: true,
        premium_since: new Date().toISOString(),
      }]);
    } else {
      await supabase.from('profiles').update({
        is_premium: true,
        premium_since: new Date().toISOString(),
      }).eq('id', userId);
    }

    return true;
  } catch (error) {
    console.error('Error upgrading user:', error);
    return false;
  }
};

// ============================================================
// Inner payment form — uses Stripe hooks (must be inside <Elements>)
// ============================================================
const PaymentForm = ({ onSuccess, onCancel, amount = '$9.95' }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    setLoading(true);
    setErrorMessage('');

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          // No redirect — we handle success inline
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (error) {
        setErrorMessage(error.message || 'Payment failed. Please try again.');
        setLoading(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Optimistically upgrade user while webhook also handles it
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase.from('profiles').update({
            is_premium: true,
            premium_since: new Date().toISOString(),
          }).eq('id', user.id);
        }
        onSuccess();
      }
    } catch (err) {
      setErrorMessage(err.message || 'An unexpected error occurred.');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Stripe embedded payment element */}
      <PaymentElement
        options={{
          layout: 'tabs',
          paymentMethodOrder: ['card', 'apple_pay', 'google_pay'],
        }}
      />

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm flex items-start gap-2">
          <X className="w-4 h-4 mt-0.5 flex-shrink-0" />
          {errorMessage}
        </div>
      )}

      {/* Security note */}
      <div className="flex items-center gap-2 text-pearl-500 text-xs">
        <Lock className="w-3.5 h-3.5" />
        <span>Secured by Stripe. We never store your card details.</span>
      </div>

      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="flex-1 py-3 px-4 rounded-full border-2 border-pearl-200 text-pearl-700 font-semibold text-sm hover:bg-pearl-50 transition-all disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || loading}
          className="flex-1 py-3 px-4 rounded-full bg-gradient-to-r from-dream-500 to-lavender-500 text-white font-semibold text-sm hover:from-dream-600 hover:to-lavender-600 transition-all shadow-dream disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Processing...
            </>
          ) : (
            `Pay ${amount}/mo`
          )}
        </button>
      </div>
    </form>
  );
};

// ============================================================
// Success screen shown after payment
// ============================================================
const PaymentSuccess = ({ onDone }) => (
  <div className="text-center py-6">
    <div className="w-16 h-16 bg-gradient-to-br from-dream-400 to-lavender-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-dream">
      <CheckCircle className="w-8 h-8 text-white" />
    </div>
    <h3 className="text-pearl-900 text-xl font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
      Welcome to Premium!
    </h3>
    <p className="text-pearl-500 text-sm mb-6">
      Your account has been upgraded. Enjoy unlimited sessions and all premium features.
    </p>
    <button
      onClick={onDone}
      className="w-full py-3 px-4 rounded-full bg-gradient-to-r from-dream-500 to-lavender-500 text-white font-semibold text-sm shadow-dream hover:from-dream-600 hover:to-lavender-600 transition-all"
    >
      Start Manifesting ✨
    </button>
  </div>
);

// ============================================================
// Main exported component — handles loading client secret + rendering Elements
// ============================================================
const StripePaymentModal = ({ isOpen, onClose, onSuccess, user }) => {
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [succeeded, setSucceeded] = useState(false);

  const PRICE_ID = process.env.REACT_APP_STRIPE_PRICE_ID;

  useEffect(() => {
    if (isOpen && user && !clientSecret) {
      initPayment();
    }
  }, [isOpen, user]);

  // Reset when closed
  useEffect(() => {
    if (!isOpen) {
      setClientSecret('');
      setError('');
      setSucceeded(false);
    }
  }, [isOpen]);

  const initPayment = async () => {
    setLoading(true);
    setError('');
    try {
      const { clientSecret: cs } = await createCheckoutSession(user.id, PRICE_ID);
      setClientSecret(cs);
    } catch (err) {
      setError(err.message || 'Failed to initialize payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const elementsOptions = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#EC4899',        // dream-500
        colorBackground: '#ffffff',
        colorText: '#2B292B',           // pearl-900
        colorDanger: '#ef4444',
        fontFamily: 'Inter, system-ui, sans-serif',
        borderRadius: '12px',
        spacingUnit: '4px',
      },
    },
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-dream-400 to-lavender-400 px-6 py-5 text-white">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
              Upgrade to Premium
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-dream-100 text-sm">$9.95/month · Cancel anytime</p>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {succeeded ? (
            <PaymentSuccess onDone={() => { onSuccess(); onClose(); }} />
          ) : loading ? (
            <div className="py-10 text-center">
              <Loader2 className="w-8 h-8 text-dream-500 animate-spin mx-auto mb-3" />
              <p className="text-pearl-500 text-sm">Initializing secure payment...</p>
            </div>
          ) : error ? (
            <div className="py-6 text-center">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 text-red-700 text-sm">
                {error}
              </div>
              <button
                onClick={initPayment}
                className="text-dream-500 font-semibold text-sm hover:text-dream-600"
              >
                Try again
              </button>
            </div>
          ) : clientSecret ? (
            <Elements stripe={stripePromise} options={elementsOptions}>
              <PaymentForm
                onSuccess={() => setSucceeded(true)}
                onCancel={onClose}
              />
            </Elements>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default StripePaymentModal;
